import { Auth, Logger } from 'aws-amplify';
import React from 'react';

import UsersClient from 'src/clients/UsersClient';
import { REDIRECT_PATH_AFTER_GOOGLE_LOGIN } from 'src/constants/authConsts';
import { AppSessionData } from 'src/constants/dataTypes/sessionDataTypes';
import {
  PortalConfigContext,
  RouteContext,
  useFeatureFlags,
} from 'src/context';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useWorkspaces } from 'src/hooks/useWorkspaces';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import { StudioLogin } from 'src/routes/onboarding/setup/components/studio/StudioLogin';
import { isStudioReferrer } from 'src/routes/onboarding/setup/constants';
import { buildStudioSignupPath } from 'src/routes/onboarding/setup/utils/buildStudioSignupPath';
import { consumeStudioPostLoginResume } from 'src/routes/onboarding/setup/utils/studioPostLoginResume';
import { setProcessingCallback } from 'src/store/auth/slice';
import { logoutFromHostedUi } from 'src/utils/AuthUtils';
import { CognitoUserWithAttributes } from 'src/utils/CognitoUtils';
import { removeSpaceToLowerCase } from 'src/utils/StringUtils';

import { OnboardingPageLayout } from '../OnboardingPageLayout';

import { OnboardingAuthLayout } from './OnboardingAuthLayout';
import {
  AuthCompleteInput,
  AuthenticatedPortalDataType,
} from './onboardingLoginTypes';
import { OnboardingWorkspaceSelect } from './OnboardingWorkspaceSelect';

const logger = new Logger('OnboardingLoginPage');

export interface queryParams {
  next?: string;
  step?: string;
  email?: string;
  poolId?: string;
  auth?: string; // one time password used when activating user
  action?: string;
  assembly?: 'true';
  referrer?: string;
  magic_link?: string;
}

/**
 * When this component is loaded we need to decide whether to show the login page or the workspace select page.
 * The way we decide is by checking the portal-sessions cookie passed in the window
 *
 * We need to show a login page if all sessions are expired or if there are no sessions. In this case a user needs to login.
 * and get a new session. If there is a session that is not expired we can go to the next step
 *
 * Once we have a session that is not expired we can make an api call to /GET /portals/...
 * If there is only one workspace we do not need to show the workspace select component
 * and we can directly go to /portal/portalId. Otherwise we need to show the workspace select component where the user selects
 * a workspace and then we go to /portal/portalId
 *
 * @returns renders the login to portal page and subsequent select workspace component
 */
export const OnboardingLoginPage = () => {
  const [authenticatedPortalsData, setAuthenticatedPortalsData] =
    React.useState<AuthCompleteInput | null>(null);
  const portalConfig = React.useContext(PortalConfigContext);
  const { query }: { query: queryParams } = React.useContext(RouteContext);
  const { EnableStudioOnboarding } = useFeatureFlags();
  const { getWorkspaces } = useWorkspaces();

  // Studio (internal) users log in passwordlessly on a dedicated Studio-styled
  // screen. Flag opens Studio login for classic traffic; referrer keeps Studio
  // marketing traffic on Studio even when the flag is off.
  const isStudioLogin =
    EnableStudioOnboarding || isStudioReferrer(query.referrer);

  // after auth is complete, we load workspaces
  // this is used to track the api call as well as the
  // computation to determine the next step
  // this is used to show a progress indicator meanwhile
  const [isProcessingWorkspace, setProcessingWorkspace] = React.useState(false);

  // Select primitives individually so each call returns a stable reference.
  // A single selector returning a new object literal trips react-redux v9's
  // stability check and warns on every render (incl. during SSR).
  const googleSignedIn = useAppSelector((state) => state.auth.signedIn);
  const googleSignInProgress = useAppSelector(
    (state) => state.auth.processingCallback,
  );

  const dispatch = useAppDispatch();

  const nextPathForGoogleLogin = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // for google login, the next path is stored in the session storage
    const googleRedirectPath = window.sessionStorage.getItem(
      REDIRECT_PATH_AFTER_GOOGLE_LOGIN,
    );

    if (googleRedirectPath) {
      // if a next param is set in the storage and not in the url
      // then we update the url to set the next param
      const pageParams = new URLSearchParams(window.location.search);
      if (!pageParams.has('next')) {
        pageParams.set('next', googleRedirectPath);

        // this is used so a page refresh is not done by the browser
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}?${pageParams}`,
        );
      }

      window.sessionStorage.removeItem(REDIRECT_PATH_AFTER_GOOGLE_LOGIN);
    }
  }, []);

  /**
   * When login is complete and a workspace is chosen this function will
   * go to the selected workspace. There are two cases:
   * 1. The user is logging in with new credentials and we need to create a new session
   * 2. The user is selecting a portal that they had previously logged into and we can use the existing session
   * @param email user email
   * @param password user password
   * @param portalData authenticated portal data
   */
  const handleWorkspaceSelected = async (
    portalData: AuthenticatedPortalDataType,
  ) => {
    if (!authenticatedPortalsData?.authSession) {
      return;
    }

    /**
     * Stores the authenticated workspaces (portals) in localStorage.
     * This allows the app to recognize which workspaces the user can access
     * and redirect them appropriately without triggering a new authentication flow.
     */
    window.localStorage.setItem(
      'authenticatedPortals',
      JSON.stringify(authenticatedPortalsData.authenticatedPortals),
    );

    const { authSession, authenticatedPortals } = authenticatedPortalsData;
    let existingAuthSession: AuthenticatedPortalDataType | undefined;
    // check if the selected workspaces is already in an existing session
    // AND the email used at login is different than the email in the existing session.
    // If the email is the same then we should continue with creating a new session to
    // replace and expired session information
    Object.entries(authenticatedPortals).forEach(
      ([email, authPortalsByEmail]) =>
        Object.values(authPortalsByEmail).forEach((authPortal) => {
          if (authPortal.id === portalData.id && authSession.email !== email) {
            // select workspace found with different email than the one used to login
            existingAuthSession = authPortal;
          }
        }),
    );

    const nextPathValue = new URLSearchParams(window.location.search).get(
      'next',
    );
    const nextPath = nextPathValue ? `?next=${nextPathValue}` : '';

    if (existingAuthSession) {
      // if there is an existing auth session we can directly navigate there
      // once portal session is set we will use nextPath for redirection
      window.location.href = `/portal/${existingAuthSession.id}${nextPath}`;
      return;
    }

    try {
      const result = await UsersClient.setWorkspace({
        ...authSession,
        portalId: portalData.id,
        hostname: portalData.hostname,
      });
      if (result.data.redirect && authSession.userId) {
        window.location.href = `${result.data.redirect}${nextPath}`;
      }
    } catch (err) {
      console.error('Error setting workspace', err);
    }
  };

  // Helper to select workspace, set session, and redirect
  const redirectToPortalWithSession = async ({
    portalData,
    sessionData,
    userId,
    nextPath,
  }: {
    portalData: AuthenticatedPortalDataType;
    sessionData: AppSessionData;
    userId: string;
    nextPath?: string | null;
  }) => {
    window.localStorage.setItem(
      `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
      userId,
    );
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();

    await UsersClient.setUserSession({
      ...sessionData,
      portalId: portalData.id,
      accessToken,
    });

    let queryParamString = '';
    if (nextPath) {
      queryParamString =
        nextPath.startsWith('?') || nextPath.startsWith('&')
          ? nextPath
          : `?next=${nextPath}`;
    }
    window.location.replace(`/portal/${portalData.id}${queryParamString}`);
  };

  /**
   * Redirects the user to the correct workspace after login.
   *
   * It checks the URL for a `next` path (like `/portal/:id`),
   * finds that workspace in localStorage,
   * and if the user already has a valid session,
   * it takes them straight there instead of showing the login page again.
   */

  const redirectToAuthenticatedPortal = async () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const nextPath = queryParams.get('next');

      if (nextPath && nextPath.startsWith('/portal/')) {
        const [nextPathParts] = nextPath.split('?');
        const portalId = nextPathParts.replace('/portal/', '');

        const storedPortals: AuthenticatedPortalDataType = JSON.parse(
          window.localStorage.getItem('authenticatedPortals') || '{}',
        );

        let matchingPortalData: AuthenticatedPortalDataType | undefined;
        Object.entries(storedPortals).forEach(([, portalsById]) => {
          if (portalsById[portalId]) {
            matchingPortalData = portalsById[portalId];
          }
        });

        if (matchingPortalData) {
          try {
            const session = await Auth.currentSession();
            const user = await Auth.currentAuthenticatedUser();
            if (session && user) {
              // Only set loading when we are about to redirect
              setProcessingWorkspace(true);
              const sessionData = { portalId: matchingPortalData.id };
              await redirectToPortalWithSession({
                portalData: matchingPortalData,
                sessionData,
                userId: user.getUsername(),
                nextPath,
              });
              return;
            }
          } catch (err) {
            console.error('No valid session, proceeding to login', err);
          }
        }
      }
    } catch (err) {
      console.error('Error checking stored portals', err);
    }
  };

  /**
   * When the onboarding login page is mounted,
   * this function is called to decide whether
   * to show the workspace select component or the login component.
   */

  const loadWorkspaces = async () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      logger.info('[AUTH_DEBUG] loadWorkspaces started');
      setProcessingWorkspace(true);
      const {
        portalIdToSession,
        workspaces: workspacesResponse,
        userNotFound,
      } = await getWorkspaces({ querySession: true });
      logger.info('[AUTH_DEBUG] getWorkspaces result:', {
        portalIdToSessionKeys: Object.keys(portalIdToSession),
        workspacesResponseKeys: Object.keys(workspacesResponse),
        userNotFound,
      });

      // Studio select-email re-auth stashes a validated /signup?referrer=studio…
      // resume path and uses next=/workspaces because nested query params are
      // unreliable on IU login. Require an explicit Studio referrer — not just
      // the rollout flag — so a later bare /login (flag on) in the same tab
      // cannot pick up an abandoned resume. Select-email always redirects via
      // buildStudioLoginPath, which sets referrer=studio.
      if (isStudioReferrer(query.referrer)) {
        const studioResumePath = consumeStudioPostLoginResume();
        if (studioResumePath) {
          setProcessingWorkspace(false);
          window.location.replace(studioResumePath);
          return;
        }
      }

      // User exists in Cognito (Google SSO) but has no DynamoDB record yet.
      // Redirect to onboarding so they can complete workspace creation.
      if (userNotFound) {
        logger.info(
          '[AUTH_DEBUG] userNotFound, redirecting to onboarding signup',
        );
        setProcessingWorkspace(false);
        const nextParam = query.next
          ? `&next=${encodeURIComponent(query.next)}`
          : '';
        window.location.href = `/signup?authSource=Google${nextParam}`;
        return;
      }

      // A Studio user who authenticated via magic link but has not yet created a
      // workspace should finish onboarding, not stall on an empty workspace picker.
      if (isStudioLogin && Object.keys(workspacesResponse).length === 0) {
        window.location.replace(
          buildStudioSignupPath({ workspaceSetup: true, email: query.email }),
        );
        return;
      }

      if (Object.keys(portalIdToSession).length === 0) {
        logger.info('[AUTH_DEBUG] portalIdToSession is empty, returning early');
        setAuthenticatedPortalsData(null);
        setProcessingWorkspace(false);
        return;
      }

      const {
        // workspace_login is a specific session type that is used for the workspace login flow
        workspace_login: workspaceLoginSession,
        ...portalSessions
      } = portalIdToSession;

      const nextPath = new URLSearchParams(window.location.search).get('next');

      // if we have portal id available in query
      // redirect user to the portal
      if (nextPath && query.email) {
        // checking if the query next url redirects to a portal
        if (nextPath.includes('/portal/')) {
          // get portal id from query next
          const [nextPathParts] = nextPath.split('?'); // remove query params to extract portal id;
          const portalId = nextPathParts.replace('/portal/', '');

          const portalData = workspacesResponse[query.email][portalId];
          try {
            await UsersClient.setWorkspace({
              ...workspaceLoginSession,
              portalId: portalData.id,
              hostname: portalData.hostname,
            });
            window.location.replace(nextPath);
            return;
          } catch (err) {
            console.error('Error setting workspace', err);
          }
        }
      }
      if (nextPath && nextPath.startsWith('/portal/')) {
        const [nextPathParts] = nextPath.split('?'); // Extract base path, ignore extra query params
        const portalId = nextPathParts.replace('/portal/', '');

        // Search for matching portalId across all emails/workspaces
        let matchingPortalData: AuthenticatedPortalDataType | undefined;
        Object.entries(workspacesResponse).forEach(([, portals]) => {
          if (portals[portalId]) {
            matchingPortalData = portals[portalId];
          }
        });

        if (matchingPortalData) {
          try {
            await redirectToPortalWithSession({
              portalData: matchingPortalData,
              sessionData: portalSessions[matchingPortalData.id] || {},
              userId: workspaceLoginSession.userId || '',
              nextPath,
            });
            return;
          } catch (err) {
            console.error('Error setting workspace for specified portal', err);
          }
        }
        // If no match, fall through to standard logic (e.g., show selection if multiple)
      }

      // when there is only email in the workspace response map
      if (Object.keys(workspacesResponse).length === 1) {
        const [email] = Object.keys(workspacesResponse);
        // and only one auth portal data object inside that email object
        // we can directly go to the portal
        if (Object.keys(workspacesResponse[email]).length === 1) {
          const [portalData] = Object.values(workspacesResponse[email]);
          const portalId = portalData.id;
          const defaultPortalSessionData = portalSessions[portalId];

          // if there is only one portal workspace found then by default the userId
          // associated with the portal is the same as the one used to login
          const { userId } = workspaceLoginSession;
          if (!userId) {
            throw new Error('User Id is undefined');
          }

          await redirectToPortalWithSession({
            portalData,
            sessionData: defaultPortalSessionData,
            userId,
            nextPath,
          });
          return;
        }
      }

      setAuthenticatedPortalsData({
        authSession: workspaceLoginSession,
        authenticatedPortals: workspacesResponse,
      });
      setProcessingWorkspace(false);
    } catch (error) {
      setProcessingWorkspace(false);
      logger.error('[AUTH_DEBUG] Error in loadWorkspaces:', error);
      console.error('Error while setting workspaces', error);
    }
  };

  const handleAuthSuccess = () => {
    // save authenticated portal in local storage
    // so that we can list them in the workspace dropdown menu
    logger.info(
      '[AUTH_DEBUG] handleAuthSuccess called, calling loadWorkspaces',
    );
    loadWorkspaces();
  };

  const completeGoogleLogin = async () => {
    logger.info('[AUTH_DEBUG] completeGoogleLogin started');
    try {
      let user: CognitoUserWithAttributes | undefined;
      try {
        user = await Auth.currentAuthenticatedUser();
        logger.info(
          '[AUTH_DEBUG] Got authenticated user:',
          user?.getUsername(),
        );
      } catch (error) {
        logger.error(
          '[AUTH_DEBUG] Failed to get authenticated user, reloading:',
          error,
        );
        console.error('failed to get the current authenticated user', error);
        // this could happen due to some unknown race condition with amplify
        // a reload usually solves the issue
        window.location.reload();
        return;
      }

      if (!user) {
        // There have been sentry errors where `user` is undefined. This safeguards against that.
        // Let's reload the page to see if that fixes the issue, per the pattern established above.
        window.location.reload();
        return;
      }

      let email: string;

      if (user.attributes) {
        /* eslint-disable-next-line prefer-destructuring */
        email = user.attributes.email;
      } else {
        // In Electron, getUserData() can fail with a network error leaving
        // user.attributes undefined. Fall back to the ID token payload.
        const session = await Auth.currentSession();
        /* eslint-disable-next-line prefer-destructuring */
        email = session.getIdToken().payload.email;
      }

      // if the email query param is set, we want to confirm the login against a valid user
      const emailInvitedFrom = query.email;
      if (emailInvitedFrom && emailInvitedFrom !== email) {
        // if the emails don't match, this is not a valid email
        // so we don't want to continue the process and show a error message
        //
        // also if there is a mismatch here, we want to make sure the user can select a different
        // google account when trying again
        // so, we log this user account out this will redirect the user to the login page
        logoutFromHostedUi(
          portalConfig,
          `${window.location.origin}/login?app-error=invite_login_mismatch`,
        );
      }

      email = removeSpaceToLowerCase(email);

      await UsersClient.setAppUserCookie(
        user,
        'workspace_login',
        'workspaces',
        email,
      );
      logger.info('[AUTH_DEBUG] setAppUserCookie completed');

      window.localStorage.setItem(
        `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
        user.getUsername(),
      );

      dispatch(setProcessingCallback(false));
    } catch (err) {
      dispatch(setProcessingCallback(false));
      console.error(err);
      throw err;
    }
  };

  React.useEffect(() => {
    redirectToAuthenticatedPortal();
  }, []);

  React.useEffect(() => {
    // Look up the user and complete the authentication flow, if the user has been signed in.
    // this is done when
    // - when the event for sign is triggerred and googleSignInProgress is set.
    //   googleSignInProgress is set when it first dispatches the processingCallback event.
    // - we want to confirm a login after signup, this is done after a signup with google.
    //   This is done so that the token can be refreshed with the permissions for the created protal
    //   and the user from db
    logger.info('[AUTH_DEBUG] Effect check:', {
      googleSignedIn,
      googleSignInProgress,
      action: query.action,
    });
    if (
      (googleSignedIn && googleSignInProgress) ||
      query.action === 'confirm-login'
    ) {
      nextPathForGoogleLogin();

      // query params have been read, we clear them out
      if (query.action === 'confirm-login') {
        window.history.replaceState(null, '', window.location.pathname);
      }
      completeGoogleLogin()
        .then(() => {
          logger.info(
            '[AUTH_DEBUG] completeGoogleLogin finished, calling handleAuthComplete',
          );
          handleAuthSuccess();
        })
        .catch((err) => {
          logger.error('[AUTH_DEBUG] completeGoogleLogin failed:', err);
          console.error('google login failed', err);
        });
    }
  }, [googleSignedIn, googleSignInProgress, query]);

  // on ssr the window location is not available
  // at this point the query params won't be available
  // these query params are used to show the loading state
  // when they are undefined the loading state wont be shown
  // and that would cause a flicker on the page.
  // this workaround will prevent rendering the component
  // until the query params are available.
  // TODO: The right solution is to use the route context
  // which holds the query params even on ssr.
  if (typeof window === 'undefined') return null;

  // if the code and state query params are set in the url, then we are
  // receiving a auth callback from google and the loading indicator
  // should be present
  // this is not done via route context since it does not update the query params when amplify clears it
  const queryParams = new URLSearchParams(window.location.search);

  const isGoogleRedirect = queryParams.has('code') && queryParams.has('state');

  const isLoading =
    googleSignInProgress ||
    isProcessingWorkspace ||
    queryParams.get('action') === 'confirm-login' ||
    isGoogleRedirect;

  return (
    <OnboardingPageLayout data-test-id="internal-login-page">
      <LoadingWrapper hideContentWhileLoading isLoading={isLoading}>
        {!authenticatedPortalsData ? (
          isStudioLogin ? (
            <StudioLogin onAuthComplete={handleAuthSuccess} />
          ) : (
            <OnboardingAuthLayout onAuthComplete={handleAuthSuccess} />
          )
        ) : (
          <OnboardingWorkspaceSelect
            onWorkspaceSelected={handleWorkspaceSelected}
            authenticatedPortals={authenticatedPortalsData.authenticatedPortals}
          />
        )}
      </LoadingWrapper>
    </OnboardingPageLayout>
  );
};
